import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	useActivitiesCache,
	useCacheMutations,
	useTasksCache,
} from "@/shared/cache";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { logger } from "@/shared/utils/logger";
import { useApi } from "@/shared/utils/request";
import { Banner } from "@/v2/components/ui/banner";
import { Button } from "@/v2/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/v2/components/ui/card";
import { Input } from "@/v2/components/ui/input";
import { Label } from "@/v2/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/v2/components/ui/select";
import { Switch } from "@/v2/components/ui/switch";
import { Textarea } from "@/v2/components/ui/textarea";
import {
	BILLABLE_CATEGORIES,
	BILLING_CATEGORIES,
	DEFAULT_MATTER_VALUES,
	type MatterFormValues,
	matterFormSchema,
} from "./matterSchema";

type CreatedMatter = {
	object: "matter";
	matterId: string;
	name: string;
	client: string;
};

const BILLING_LABELS: Record<(typeof BILLING_CATEGORIES)[number], string> = {
	hourly: "Hourly",
	flat_rate: "Flat rate",
	pro_bono: "Pro bono",
	administrative: "Administrative",
	internal: "Internal",
	unknown: "Unknown",
};

const BILLABLE_LABELS: Record<(typeof BILLABLE_CATEGORIES)[number], string> = {
	billable: "Billable",
	non_billable: "Non-billable",
	billable_credit: "Billable credit",
	unknown: "Unknown",
};

const NO_GROUP = "__none__";
const MATTER_FORM_FIELDS = new Set<keyof MatterFormValues>([
	"matterId",
	"client",
	"name",
	"description",
	"status",
	"taskGroup",
	"activityGroup",
	"billable",
	"visibilityMode",
	"selectabilityMode",
	"billingCategory",
	"billableCategory",
]);

function FieldError({ id, message }: { id: string; message?: string }) {
	if (!message) return null;
	return (
		<p id={id} className="text-xs text-destructive" role="alert">
			{message}
		</p>
	);
}

function emptyToNull(value: string): string | null {
	return value.trim() || null;
}

export default function AdminMatters() {
	useDocumentTitle("Admin Matters");
	const { makeApiRequest } = useApi();
	const { refresh } = useCacheMutations();
	const { tasks, isLoading: tasksLoading } = useTasksCache();
	const { activities, isLoading: activitiesLoading } = useActivitiesCache();
	const taskGroups = useMemo(
		() =>
			[...new Set(tasks.map((task) => task.taskGroup).filter(Boolean))].sort(),
		[tasks],
	);
	const activityGroups = useMemo(
		() =>
			[
				...new Set(
					activities.map((activity) => activity.activityGroup).filter(Boolean),
				),
			].sort(),
		[activities],
	);
	const [apiError, setApiError] = useState<string | null>(null);
	const [createdMatter, setCreatedMatter] = useState<CreatedMatter | null>(
		null,
	);

	const {
		register,
		control,
		handleSubmit,
		reset,
		setError,
		formState: { errors, isSubmitting },
	} = useForm<MatterFormValues>({
		resolver: zodResolver(matterFormSchema),
		defaultValues: DEFAULT_MATTER_VALUES,
	});

	const onSubmit = handleSubmit(async (values) => {
		setApiError(null);
		setCreatedMatter(null);

		try {
			const response = await makeApiRequest({
				endpoint: "/v2/matters",
				method: "POST",
				headers: { "Idempotency-Key": crypto.randomUUID() },
				body: {
					matterId: values.matterId,
					client: values.client,
					name: values.name,
					description: values.description,
					status: emptyToNull(values.status),
					taskGroup: emptyToNull(values.taskGroup),
					activityGroup: emptyToNull(values.activityGroup),
					billable: values.billable,
					visibilityMode: values.visibilityMode,
					selectabilityMode: values.selectabilityMode,
					billingCategory: values.billingCategory,
					billableCategory: values.billableCategory,
				},
			});

			if (response.status === "ERROR") {
				const errorParam = response.apiError?.param as
					| keyof MatterFormValues
					| undefined;
				if (errorParam && MATTER_FORM_FIELDS.has(errorParam)) {
					setError(errorParam, {
						type: "server",
						message: response.error ?? "This value is invalid.",
					});
				} else if (response.apiError?.code === "MATTER_ALREADY_EXISTS") {
					setError("matterId", {
						type: "server",
						message: "A matter with this ID already exists.",
					});
				}
				setApiError(response.error ?? "Unable to create matter.");
				return;
			}

			const created = response.data as CreatedMatter;
			setCreatedMatter(created);
			reset(DEFAULT_MATTER_VALUES);
			toast.success(`Matter ${created.matterId} created`);

			try {
				await refresh("matters");
			} catch (error) {
				logger.warn(
					"Matter created but the local matter cache did not refresh",
					{
						error: error instanceof Error ? error.message : String(error),
						matterId: created.matterId,
					},
				);
			}
		} catch (error) {
			logger.captureException(
				error instanceof Error ? error : new Error(String(error)),
				{ action: "create_matter" },
			);
			setApiError("Unable to create matter. Please try again.");
		}
	});

	return (
		<div className="mx-auto w-full max-w-5xl p-6 lg:p-10">
			<div className="mb-8">
				<h1 className="text-2xl font-semibold tracking-tight">Matters</h1>
				<p className="mt-2 max-w-2xl text-sm text-muted-foreground">
					Create a firm-wide matter that timekeepers can use when tracking and
					exporting time.
				</p>
			</div>

			{createdMatter && (
				<div className="mb-6 flex items-start gap-3 rounded-lg border border-success/30 bg-success/10 p-4">
					<CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" />
					<div>
						<p className="text-sm font-medium">Matter created</p>
						<p className="mt-1 text-sm text-muted-foreground">
							{createdMatter.matterId} · {createdMatter.name} for{" "}
							{createdMatter.client}
						</p>
					</div>
				</div>
			)}

			{apiError && (
				<Banner
					variant="error"
					title="Matter could not be created"
					className="mb-6"
				>
					{apiError}
				</Banner>
			)}

			<form onSubmit={onSubmit} noValidate className="space-y-6">
				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Matter details</CardTitle>
						<CardDescription>
							The ID must be unique within the firm. Name is stored as the
							matter name.
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-5 md:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="matterId">Matter ID</Label>
							<Input
								id="matterId"
								placeholder="e.g. 10001.001"
								aria-invalid={Boolean(errors.matterId)}
								aria-describedby={
									errors.matterId ? "matterId-error" : undefined
								}
								{...register("matterId")}
							/>
							<FieldError
								id="matterId-error"
								message={errors.matterId?.message}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="client">Client</Label>
							<Input
								id="client"
								placeholder="Client name"
								aria-invalid={Boolean(errors.client)}
								aria-describedby={errors.client ? "client-error" : undefined}
								{...register("client")}
							/>
							<FieldError id="client-error" message={errors.client?.message} />
						</div>
						<div className="space-y-2 md:col-span-2">
							<Label htmlFor="name">Name</Label>
							<Input
								id="name"
								placeholder="Matter name"
								aria-invalid={Boolean(errors.name)}
								aria-describedby={errors.name ? "name-error" : undefined}
								{...register("name")}
							/>
							<FieldError id="name-error" message={errors.name?.message} />
						</div>
						<div className="space-y-2 md:col-span-2">
							<Label htmlFor="description">Description</Label>
							<Textarea
								id="description"
								placeholder="Describe the matter"
								rows={4}
								aria-invalid={Boolean(errors.description)}
								aria-describedby={
									errors.description ? "description-error" : undefined
								}
								{...register("description")}
							/>
							<FieldError
								id="description-error"
								message={errors.description?.message}
							/>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Status and billing</CardTitle>
						<CardDescription>
							Set reporting categories independently from whether the matter is
							billable.
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-5 md:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="status">Status</Label>
							<Input
								id="status"
								placeholder="e.g. Active"
								aria-invalid={Boolean(errors.status)}
								aria-describedby={errors.status ? "status-error" : undefined}
								{...register("status")}
							/>
							<FieldError id="status-error" message={errors.status?.message} />
							<p className="text-xs text-muted-foreground">
								Optional; use the firm’s status wording. Some firms only show
								active statuses in matter pickers.
							</p>
						</div>
						<div className="flex items-center justify-between gap-4 rounded-lg border p-4">
							<div>
								<Label htmlFor="billable">Billable</Label>
								<p className="mt-1 text-xs text-muted-foreground">
									Allow time on this matter to be billed.
								</p>
							</div>
							<Controller
								name="billable"
								control={control}
								render={({ field }) => (
									<Switch
										id="billable"
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="billingCategory">Billing category</Label>
							<Controller
								name="billingCategory"
								control={control}
								render={({ field }) => (
									<Select value={field.value} onValueChange={field.onChange}>
										<SelectTrigger id="billingCategory">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{BILLING_CATEGORIES.map((value) => (
												<SelectItem key={value} value={value}>
													{BILLING_LABELS[value]}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="billableCategory">Billability category</Label>
							<Controller
								name="billableCategory"
								control={control}
								render={({ field }) => (
									<Select value={field.value} onValueChange={field.onChange}>
										<SelectTrigger id="billableCategory">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{BILLABLE_CATEGORIES.map((value) => (
												<SelectItem key={value} value={value}>
													{BILLABLE_LABELS[value]}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Codes and access</CardTitle>
						<CardDescription>
							Optional code groups control task and activity choices. Deny-list
							mode makes the matter firm-wide unless a professional is
							explicitly denied.
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-5 md:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="taskGroup">Task group</Label>
							<Controller
								name="taskGroup"
								control={control}
								render={({ field }) => (
									<Select
										value={field.value || NO_GROUP}
										onValueChange={(value) =>
											field.onChange(value === NO_GROUP ? "" : value)
										}
										disabled={tasksLoading}
									>
										<SelectTrigger
											id="taskGroup"
											aria-invalid={Boolean(errors.taskGroup)}
											aria-describedby={
												errors.taskGroup ? "taskGroup-error" : undefined
											}
										>
											<SelectValue placeholder="No task group" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={NO_GROUP}>No task group</SelectItem>
											{taskGroups.map((group) => (
												<SelectItem key={group} value={group}>
													{group}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
							<FieldError
								id="taskGroup-error"
								message={errors.taskGroup?.message}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="activityGroup">Activity group</Label>
							<Controller
								name="activityGroup"
								control={control}
								render={({ field }) => (
									<Select
										value={field.value || NO_GROUP}
										onValueChange={(value) =>
											field.onChange(value === NO_GROUP ? "" : value)
										}
										disabled={activitiesLoading}
									>
										<SelectTrigger
											id="activityGroup"
											aria-invalid={Boolean(errors.activityGroup)}
											aria-describedby={
												errors.activityGroup ? "activityGroup-error" : undefined
											}
										>
											<SelectValue placeholder="No activity group" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={NO_GROUP}>
												No activity group
											</SelectItem>
											{activityGroups.map((group) => (
												<SelectItem key={group} value={group}>
													{group}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
							<FieldError
								id="activityGroup-error"
								message={errors.activityGroup?.message}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="visibilityMode">Visibility</Label>
							<Controller
								name="visibilityMode"
								control={control}
								render={({ field }) => (
									<Select value={field.value} onValueChange={field.onChange}>
										<SelectTrigger id="visibilityMode">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="deny_list">
												Firm-wide (deny list)
											</SelectItem>
											<SelectItem value="allow_list">
												Restricted (allow list)
											</SelectItem>
										</SelectContent>
									</Select>
								)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="selectabilityMode">Selectability</Label>
							<Controller
								name="selectabilityMode"
								control={control}
								render={({ field }) => (
									<Select value={field.value} onValueChange={field.onChange}>
										<SelectTrigger id="selectabilityMode">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="deny_list">
												Firm-wide (deny list)
											</SelectItem>
											<SelectItem value="allow_list">
												Restricted (allow list)
											</SelectItem>
										</SelectContent>
									</Select>
								)}
							/>
						</div>
					</CardContent>
				</Card>

				<div className="flex items-center justify-end gap-3 pb-8">
					<Button
						type="button"
						variant="outline"
						disabled={isSubmitting}
						onClick={() => {
							reset(DEFAULT_MATTER_VALUES);
							setApiError(null);
							setCreatedMatter(null);
						}}
					>
						Clear
					</Button>
					<Button type="submit" loading={isSubmitting}>
						Create matter
					</Button>
				</div>
			</form>
		</div>
	);
}
